<template>
  <div class="card_bg">
    <div class="ready_card">
      <div class="ready_line_group">
        <span class="ready_line_prefix">{{$t('message.transfer_prefix_before')}}</span>
        <span class="ready_line_token">{{ getTokenDesc }}</span>
        <span class="ready_line_suffix">{{$t('message.transfer_prefix_after')}}</span>
      </div>

      <div class="ready_row memo_row">
        <span class="memo_text">{{$t('message.address')}}: {{ getDepositAddress() }}</span>
        <button
          type="button"
          class="copy_icon_btn"
          :title="$t('message.copy_address')"
          @click="copyToClipboard(getDepositAddress())"
        >
          <span class="copy_icon" aria-hidden="true"></span>
        </button>
      </div>

      <div class="ready_row memo_row" v-if="checkIsNeedMemo()">
        <span class="memo_text">MEMO: {{ getTransferMemo() }}</span>
        <button
          type="button"
          class="copy_icon_btn"
          :title="$t('message.copy_memo')"
          @click="copyToClipboard(getTransferMemo())"
        >
          <span class="copy_icon" aria-hidden="true"></span>
        </button>
      </div>

      <div class="ready_row">
        <span class="ready_text">{{ getSwapAddressDesc() }}: {{ getSwapAddress() }}</span>
      </div>

      <div class="memo_warn_text" v-if="checkIsNeedMemo()">
        {{$t('message.memo_warning')}}
      </div>
      <div class="memo_warn_text">
        {{$t('message.paste_warning')}}
      </div>
    </div>

    <fee-notice v-if="serviceFee > 0" :token="getTokenDesc" :fee="serviceFee" />
    <div v-if="copyToastVisible" class="copy_toast">{{$t('message.copy_success')}}</div>
  </div>
</template>

<script>
import utils from '../../common/v2/utils'
import feeNotice from './feeNotice.vue'

export default {
  name: 'depositInfoView',
  components: { feeNotice },
  props: {
    depositInfo: {
      type: Object,
      default: function () {
        return { SwapType: -1, depositAddress: '', memo: '', swapAddress: '', exChangeBnbMemo: '' }
      }
    }
  },
  data () {
    return {
      copyToastVisible: false,
      copyToastTimer: null
    }
  },
  computed: {
    getTokenDesc () {
      const swapType = this.depositInfo[utils.swapTypeKey]
      const key = utils.getSourceTokenKey(swapType)
      return key ? this.$t(key) : ''
    },
    serviceFee () {
      return utils.serviceFee(this.depositInfo[utils.swapTypeKey])
    }
  },
  methods: {
    getDepositAddress () {
      return this.depositInfo[utils.addressKey] || ''
    },
    getTransferMemo () {
      return this.depositInfo[utils.memoKey] || this.depositInfo.exChangeBnbMemo || 'N/A'
    },
    checkIsNeedMemo () {
      const swapType = this.depositInfo[utils.swapTypeKey]
      const source = utils.getSourceChain(swapType)
      // ERC-20 and BEP-20 deposits don't require memo
      return source !== null && source !== 'erc20' && source !== 'bep20'
    },
    getSwapAddressDesc () {
      const swapType = this.depositInfo[utils.swapTypeKey]
      const key = utils.getTargetAddressKey(swapType)
      return key ? this.$t(key) : ''
    },
    getSwapAddress () {
      return this.depositInfo[utils.swapAddressKey] || ''
    },
    async copyToClipboard (text) {
      if (!text) return
      try {
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
          this.showCopySuccess()
          return
        }
      } catch (e) {}

      // Fallback for environments without Clipboard API
      const el = document.createElement('textarea')
      el.value = text
      el.setAttribute('readonly', '')
      el.style.position = 'absolute'
      el.style.left = '-9999px'
      document.body.appendChild(el)
      try {
        el.select()
        document.execCommand('copy')
        this.showCopySuccess()
      } finally {
        document.body.removeChild(el)
      }
    },
    showCopySuccess () {
      this.copyToastVisible = true
      if (this.copyToastTimer) {
        clearTimeout(this.copyToastTimer)
      }
      this.copyToastTimer = setTimeout(() => {
        this.copyToastVisible = false
        this.copyToastTimer = null
      }, 1200)
    }
  },
  beforeDestroy () {
    if (this.copyToastTimer) {
      clearTimeout(this.copyToastTimer)
      this.copyToastTimer = null
    }
  }
}
</script>

<style scoped>
.card_bg {
  width: 100%;
  max-width: 620px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 24px;
}

.ready_card {
  border: 1px solid #e4e4e7;
  border-radius: 0;
  background: #fff;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ready_line_group {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #71717a;
  font-size: 14px;
  font-weight: 500;
}

.ready_line_token {
  font-weight: 700;
}

.ready_row {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #71717a;
  font-size: 14px;
  min-width: 0;
  word-break: break-all;
}

.memo_row .memo_text {
  color: #18181b;
  font-weight: 600;
  min-width: 0;
  word-break: break-all;
}

.value_text {
  color: #18181b;
}

.copy_icon {
  display: inline-block;
  width: 16px;
  height: 16px;
  background: linear-gradient(90deg, #6a11cb 0%, #2575fc 100%);
  -webkit-mask-image: url('../../assets/img/content-copy.svg');
  mask-image: url('../../assets/img/content-copy.svg');
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
}

.copy_icon_btn {
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 1;
}

.copy_toast {
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: 200;
  background: #18181b;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 12px;
  border-radius: 0;
  box-shadow: 0 6px 16px #00000026;
}

.addr_label {
  color: #18181b;
  font-weight: 700;
  font-size: 14px;
}

.memo_warn_text {
  color: #dc2626;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
}

@media (max-width: 768px) {
  .card_bg {
    width: 100%;
  }
}

@media (max-width: 640px) {
  .ready_card {
    padding: 14px;
    gap: 12px;
  }
  .ready_line_group {
    font-size: 13px;
  }
  .ready_row {
    font-size: 13px;
  }
  .addr_label {
    font-size: 13px;
  }
  .memo_warn_text {
    font-size: 12px;
  }
}
</style>
